import axios from "axios";
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { faExclamation } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import Pending from "./Pending";

const PendingInfo = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [employeeApplication, setEmployeeApplication] = useState([]);
  const [selectedLeaveID, setSelectedLeaveID] = useState(null);

  const getLeaveInformation = async () => {
    try {
      const response = await axios.get(`${apiUrl}/leave/details/0`);
      console.log(response.data.leaves);
      // Sort by DateApplied in descending order (latest first)
      const sortedLeaves = response.data.leaves.sort((a, b) => 
        new Date(b.DateApplied) - new Date(a.DateApplied)
      );
      setEmployeeApplication(sortedLeaves);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getLeaveInformation();
  }, []);

  const handleViewApplication = (leaveID) => {
    console.log("Viewing application with ID:", leaveID);
    setSelectedLeaveID(leaveID);
  };

  const handleCloseModal = () => {
    setSelectedLeaveID(null);
  };

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm mt-6">
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#2D2D2D]">Pending Applications</h2>
            <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full font-medium">
              {employeeApplication.length} {employeeApplication.length === 1 ? 'Application' : 'Applications'}
            </span>
          </div>
          
          {employeeApplication.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FontAwesomeIcon icon={faEllipsis} className="text-5xl text-gray-400 mb-4" />
              <p className="text-lg">No pending applications</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      Employee Name
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <FontAwesomeIcon icon={faCalendarDays} className="mr-2" />
                      Applied Date
                    </th>
                    <th scope="col" className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <FontAwesomeIcon icon={faEye} className="mr-2" />
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employeeApplication.map((item, index) => (
                    <tr
                      className="hover:bg-amber-50 transition duration-150"
                      key={index}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{item.Name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{item.DateApplied}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {(() => {
                          switch (item.Status) {
                            case 0:
                              return (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  <FontAwesomeIcon icon={faEllipsis} fade className="mr-1" />
                                  Pending
                                </span>
                              );
                            case 1:
                              return (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                  Approved
                                </span>
                              );
                            case 2:
                              return (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <FontAwesomeIcon icon={faExclamation} className="mr-1" />
                                  Disapproved
                                </span>
                              );
                            default:
                              return "Unknown Status";
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button 
                          onClick={() => handleViewApplication(item.Leave_ID)}
                          className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-200 transition"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-2" />
                          View Application
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {selectedLeaveID && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-3.5 flex justify-between items-center rounded-t-lg z-10">
              <h2 className="text-lg font-semibold text-white">Leave Application Details</h2>
              <button
                onClick={handleCloseModal}
                className="text-white hover:bg-white hover:text-amber-600 rounded-full p-1.5 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <Pending id={selectedLeaveID} />
          </div>
        </div>
      )}
    </>
  );
};

export default PendingInfo;
